import { FunctionComponent, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { Room, RoomUnit } from '@spike/room-model';
import clsx from 'clsx';
import ClickAwayListener from 'components/CreateBooking/UI/ClickAwayListener';
import styles from './RoomUnitSelector.module.scss';

interface RoomUnitSelectorProps {
    rooms: Array<Room>;
    selectedUnitId?: number;
    placeholder?: string;
    className?: string;
    onSelect: (unitId: number | undefined) => void;
}

const findUnit = (rooms: Array<Room>, unitId: number | undefined): RoomUnit | undefined => {
    if (unitId === undefined) return undefined;
    for (const room of rooms) {
        const unit = room.units.find(u => u.id === unitId);
        if (unit) return unit;
    }
    return undefined;
};

export const RoomUnitSelector: FunctionComponent<RoomUnitSelectorProps> = ({
    rooms,
    selectedUnitId,
    placeholder = 'Select Room Unit',
    className,
    onSelect
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const selectedUnit = findUnit(rooms, selectedUnitId);
    const allUnits = rooms.flatMap(r => r.units);

    return (
        <ClickAwayListener onClickAway={() => setIsOpen(false)}>
            <div className={clsx(styles.container, className)}>
                <span className={styles.label}>Room</span>
                <div className={styles.dropdownContainer}>
                    <div
                        className={clsx(styles.border, {
                            [styles.collapsed]: !isOpen,
                            [styles.expanded]: isOpen
                        })}
                    >
                        <div
                            className={styles.headerContainer}
                            onClick={() => setIsOpen(prev => !prev)}
                        >
                            <div className={styles.textContainer}>
                                <span
                                    className={clsx(styles.header, {
                                        [styles.placeholder]: !selectedUnit
                                    })}
                                >
                                    {selectedUnit ? selectedUnit.name : placeholder}
                                </span>
                            </div>
                            <div className={styles.chevronContainer}>
                                <FontAwesomeIcon
                                    icon={isOpen ? faChevronUp : faChevronDown}
                                    color={isOpen ? '#222222' : '#7A7A7A'}
                                    className={styles.chevron}
                                />
                            </div>
                        </div>

                        {isOpen && (
                            <div className={styles.listContainer}>
                                {allUnits.map(unit => (
                                    <div
                                        key={unit.uuid}
                                        className={styles.item}
                                        onClick={() => {
                                            onSelect(unit.id);
                                            setIsOpen(false);
                                        }}
                                    >
                                        {unit.name}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </ClickAwayListener>
    );
};

export default RoomUnitSelector;
